import type { ScoreRow } from "./api";

const PENDING_SCORES_KEY = "traszka:pending_scores";
const LOCAL_SCORES_KEY = "traszka:local_scores";

/**
 * Save a score entry locally when offline or when backend server is unreachable.
 * Stores both in the pending sync queue and in local offline cache.
 */
export function savePendingScore(row: ScoreRow): void {
  try {
    const pending = getPendingScores();
    // Replace lower pending score for same email if present, or push new
    const existingIdx = pending.findIndex((r) => r.email === row.email);
    if (existingIdx >= 0) {
      if (row.score > pending[existingIdx].score) {
        pending[existingIdx] = row;
      }
    } else {
      pending.push(row);
    }
    localStorage.setItem(PENDING_SCORES_KEY, JSON.stringify(pending));

    // Also update local overall scores cache
    saveToLocalScoresCache(row);
  } catch (e) {
    console.error("[offlineStore] failed to save pending score:", e);
  }
}

/** Get list of pending score entries awaiting sync to Railway DB. */
export function getPendingScores(): ScoreRow[] {
  try {
    const raw = localStorage.getItem(PENDING_SCORES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Get total count of pending offline score entries. */
export function getPendingScoresCount(): number {
  return getPendingScores().length;
}

/** Clear all pending score entries after successful sync. */
export function clearPendingScores(): void {
  try {
    localStorage.removeItem(PENDING_SCORES_KEY);
  } catch (e) {
    console.error("[offlineStore] failed to clear pending scores:", e);
  }
}

/** Save a score to the local offline cache so offline runs show on local device leaderboard. */
function saveToLocalScoresCache(row: ScoreRow): void {
  try {
    const raw = localStorage.getItem(LOCAL_SCORES_KEY);
    const cached: ScoreRow[] = raw ? JSON.parse(raw) : [];
    const existingIdx = cached.findIndex((r) => r.email === row.email);
    if (existingIdx >= 0) {
      if (row.score > cached[existingIdx].score) {
        cached[existingIdx] = row;
      }
    } else {
      cached.push(row);
    }
    localStorage.setItem(LOCAL_SCORES_KEY, JSON.stringify(cached));
  } catch (e) {
    console.error("[offlineStore] failed to update local scores cache:", e);
  }
}

/** Get all locally cached scores. */
export function getLocalScoresCache(): ScoreRow[] {
  try {
    const raw = localStorage.getItem(LOCAL_SCORES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Merge remote server scores with local offline entries.
 * Deduplicates by email, retaining the highest score per email.
 */
export function mergeWithOfflineScores(remoteScores: ScoreRow[]): ScoreRow[] {
  const localScores = getLocalScoresCache();
  const byEmail = new Map<string, ScoreRow>();

  for (const r of remoteScores) {
    if (r && r.email) byEmail.set(r.email, r);
  }

  for (const l of localScores) {
    if (l && l.email) {
      const existing = byEmail.get(l.email);
      if (!existing || l.score > existing.score) {
        byEmail.set(l.email, l);
      }
    }
  }

  return Array.from(byEmail.values()).sort((a, b) => b.score - a.score);
}

/**
 * Attempt to synchronize pending offline scores with the server.
 */
export async function syncPendingScores(
  submitBatchFn: (rows: ScoreRow[]) => Promise<{ synced: number }>
): Promise<{ success: boolean; count: number; error?: string }> {
  const pending = getPendingScores();
  if (pending.length === 0) {
    return { success: true, count: 0 };
  }

  try {
    const res = await submitBatchFn(pending);
    clearPendingScores();
    return { success: true, count: res.synced ?? pending.length };
  } catch (err) {
    const msg = (err as Error).message ?? "Sync failed";
    console.warn("[offlineStore] sync failed:", msg);
    return { success: false, count: 0, error: msg };
  }
}
